// src/utils/geneCodes/buildBloodWheelData.ts
import { BloodTestResult } from "../../types/lab/BloodTestTypes";
import { BloodTestSubCategory, BloodTestCatalogueType } from "../../types/lab/BloodTestCatalogueTypes";

const UNGROUPED = "Other / Ungrouped";

// The wheel bucket — one per catalogue sub-category holding the analytes that resolved into it
export interface BloodWheelCategory {
	name: string;
	color: string | null;
	sort_order: number;
	results: BloodTestResult[];
}

// Mirrors the backend resolver's token normalisation
const normalize = (value: string): string =>
	value
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, " ")
		.trim();

interface TypeMatch {
	subCategory: BloodTestSubCategory;
	type: BloodTestCatalogueType;
}

// Every normalised token (type value + name + aliases) → its owning sub-category/type; earlier sub-categories win collisions
const buildTypeIndex = (catalogue: BloodTestSubCategory[]): Map<string, TypeMatch> => {
	const index = new Map<string, TypeMatch>();

	const ordered = [...catalogue].sort((a, b) => a.sort_order - b.sort_order);

	ordered.forEach((subCategory) => {
		(subCategory.test_types ?? []).forEach((type) => {
			const tokens = [type.value, type.name, ...(type.aliases ?? [])];
			tokens.forEach((token) => {
				if (!token) return;
				const key = normalize(token);
				if (key && !index.has(key)) {
					index.set(key, { subCategory, type });
				}
			});
		});
	});

	return index;
};

// Per analyte, first hit wins: canonical_type › test_category slug › lab name/code via the alias index (pre-backfill rows); no match → Other / Ungrouped
export const buildBloodWheelData = (results: BloodTestResult[], catalogue: BloodTestSubCategory[]): BloodWheelCategory[] => {
	const index = buildTypeIndex(catalogue);

	const orderedCatalogue = [...catalogue].sort((a, b) => a.sort_order - b.sort_order);
	const catalogueOrder = new Map<string, { color: string | null; sort_order: number }>();
	orderedCatalogue.forEach((subCategory) => {
		catalogueOrder.set(subCategory.name, { color: subCategory.color, sort_order: subCategory.sort_order });
	});

	const buckets = new Map<string, BloodTestResult[]>();
	const ensureBucket = (name: string) => {
		if (!buckets.has(name)) buckets.set(name, []);
		return buckets.get(name)!;
	};

	(results ?? []).forEach((result) => {
		const candidates = [result.canonical_type, result.test_category, result.test_name, result.test_code].filter(Boolean) as string[];

		let match: TypeMatch | undefined;
		for (const candidate of candidates) {
			match = index.get(normalize(candidate));
			if (match) break;
		}

		if (match) {
			ensureBucket(match.subCategory.name).push({
				...result,
				definition_description: match.type.description ?? null,
				definition_clinical_use: match.type.clinical_use ?? null,
			});
		} else {
			ensureBucket(UNGROUPED).push(result);
		}
	});

	const categories: BloodWheelCategory[] = Array.from(buckets.entries()).map(([name, bucketResults]) => {
		const meta = catalogueOrder.get(name);
		return {
			name,
			color: meta?.color ?? null,
			sort_order: meta ? meta.sort_order : Number.MAX_SAFE_INTEGER,
			results: [...bucketResults].sort((a, b) => a.sort_order - b.sort_order),
		};
	});

	return categories.sort((a, b) => {
		if (a.name === UNGROUPED) return 1;
		if (b.name === UNGROUPED) return -1;
		if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
		return a.name.localeCompare(b.name);
	});
};

export const UNGROUPED_CATEGORY = UNGROUPED;
